import { Component, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ApiService } from '../../shared/api.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-ad-details',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './ad-details.component.html',
  styles: [`
    .page {
      max-width: 800px;
      margin: 0 auto;
      padding: 24px;
    }

    .header-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 20px;
      flex-wrap: wrap;
      gap: 12px;
    }

    h1 {
      color: var(--text-color);
      font-size: 24px;
      margin: 0;
    }

    .card {
      background: var(--surface-color);
      border-radius: 8px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      border: 1px solid var(--border-color);
    }

    .card-header {
      padding: 16px;
      border-bottom: 1px solid var(--border-color);
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-wrap: wrap;
      gap: 12px;
    }

    .card-header h2 {
      margin: 0;
      color: var(--text-color);
      font-size: 18px;
    }

    .card-content {
      padding: 20px;
    }

    .image-container {
      margin-bottom: 20px;
      border-radius: 6px;
      overflow: hidden;
      background: var(--background-color);
    }

    .ad-image {
      width: 100%;
      max-height: 300px;
      object-fit: contain;
      display: block;
    }

    .details-grid {
      display: grid;
      gap: 16px;
    }

    .detail-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }

    .label {
      color: var(--text-muted-color);
      font-size: 14px;
    }

    .value {
      color: var(--text-color);
      word-break: break-word;
    }

    .link {
      color: var(--primary-color);
      text-decoration: none;
    }

    .link:hover {
      text-decoration: underline;
    }

    .metrics {
      display: flex;
      gap: 12px;
      margin-top: 8px;
    }

    .pill {
      background: var(--hover-color);
      color: var(--text-color);
      padding: 4px 12px;
      border-radius: 999px;
      font-size: 14px;
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }

    .error-message {
      background: #fef2f2;
      color: #b91c1c;
      padding: 12px;
      border-radius: 6px;
      margin-top: 16px;
      font-size: 14px;
    }

    .card-actions {
      padding: 16px;
      border-top: 1px solid var(--border-color);
      display: flex;
      justify-content: flex-end;
    }

    /* Buttons */
    .btn {
      display: inline-block;
      padding: 8px 12px;
      border-radius: 6px;
      text-decoration: none;
      font-weight: 600;
      font-size: 14px;
      margin-left: 8px;
    }

    .btn-ghost {
      background: transparent;
      color: var(--text-color);
      border: 1px solid transparent;
    }

    .btn-primary {
      background: var(--primary-color);
      color: #fff;
      border: 1px solid rgba(0,0,0,0.06);
    }

    .badge {
      padding: 6px 10px;
      border-radius: 999px;
      font-weight: 700;
      font-size: 13px;
      color: #fff;
    }

    .badge--active { background: #16a34a; }
    .badge--pending { background: #f59e0b; }
    .badge--blocked { background: #dc2626; }
    .badge--expired { background: #64748b; }
    .badge--draft { background: #6b7280; }

    .header-row { align-items: center; }
    .title-group { display:flex; flex-direction:column; }
    .subtitle { margin: 4px 0 0; color: var(--text-muted-color); font-size: 13px; }

    .card { overflow: visible; }

    .image-container { max-height: 360px; }

    .details-grid { grid-template-columns: 1fr 1fr; }
    .detail-item.full { grid-column: 1 / -1; }

    .pill { background: rgba(0,0,0,0.05); }

    .loading, .error {
      text-align: center;
      padding: 24px;
      color: var(--text-muted-color);
    }

    .error {
      color: #dc2626;
    }

    /* Modal Styles */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
    }

    .modal {
      background: var(--surface-color);
      border-radius: 12px;
      box-shadow: 0 20px 25px rgba(0, 0, 0, 0.15);
      max-width: 500px;
      width: 90%;
      max-height: 90vh;
      overflow-y: auto;
    }

    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px;
      border-bottom: 1px solid var(--border-color);
    }

    .modal-header h3 {
      margin: 0;
      color: var(--text-color);
      font-size: 18px;
    }

    .close-btn {
      background: none;
      border: none;
      font-size: 24px;
      cursor: pointer;
      color: var(--text-muted-color);
      padding: 0;
      width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .close-btn:hover {
      color: var(--text-color);
    }

    .modal-error {
      background: #fef2f2;
      color: #b91c1c;
      padding: 12px 20px;
      border-radius: 6px;
      margin: 16px;
      font-size: 14px;
    }

    .modal-content {
      padding: 20px;
    }

    .payment-options {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }

    .payment-btn {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 8px;
      padding: 20px;
      background: var(--hover-color);
      border: 2px solid var(--border-color);
      border-radius: 8px;
      cursor: pointer;
      transition: all 0.2s;
      color: var(--text-color);
      font-weight: 600;
    }

    .payment-btn:hover:not([disabled]) {
      border-color: var(--primary-color);
      background: rgba(138, 123, 86, 0.1);
    }

    .payment-btn[disabled] {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .payment-icon {
      font-size: 32px;
    }

    .payment-label {
      font-size: 14px;
    }

    .pix-container {
      display: flex;
      flex-direction: column;
      gap: 20px;
      align-items: center;
    }

    .pix-image {
      padding: 20px;
      background: #fff;
      border-radius: 8px;
      border: 1px solid var(--border-color);
    }

    .pix-image img {
      max-width: 100%;
      height: auto;
    }

    .pix-code {
      width: 100%;
    }

    .code-display {
      display: flex;
      gap: 8px;
      margin-top: 8px;
    }

    .code-display code {
      flex: 1;
      background: var(--background-color);
      padding: 8px 12px;
      border-radius: 4px;
      font-size: 12px;
      color: var(--text-color);
      word-break: break-all;
      font-family: monospace;
    }

    .copy-btn {
      padding: 8px 16px;
      background: var(--primary-color);
      color: #fff;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      font-weight: 600;
      font-size: 12px;
      white-space: nowrap;
    }

    .copy-btn:hover {
      opacity: 0.9;
    }

    .pix-expiration {
      text-align: center;
      color: var(--text-muted-color);
      font-size: 12px;
    }

    .modal-footer {
      padding: 16px 20px;
      border-top: 1px solid var(--border-color);
      display: flex;
      justify-content: flex-end;
      gap: 8px;
    }

    /* Credit Card Form */
    .credit-card-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .form-group {
      display: flex;
      flex-direction: column;
      gap: 6px;
    }

    .form-group label {
      color: var(--text-color);
      font-weight: 600;
      font-size: 14px;
    }

    .form-group input {
      background: var(--input-bg);
      border: 1px solid var(--input-border-color);
      color: var(--text-color);
      padding: 10px 12px;
      border-radius: 6px;
      font-size: 14px;
      font-family: inherit;
    }

    .form-group input::placeholder {
      color: var(--text-muted-color);
    }

    .form-row {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
    }

    .form-row .form-group input.half {
      flex: 1;
    }

    .expiry {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .expiry .half {
      width: 64px;
      box-sizing: border-box;
    }

    .sep {
      color: var(--text-color);
      font-weight: 700;
      font-size: 16px;
      line-height: 1;
    }

    .form-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 8px;
    }

    .form-actions .btn {
      padding: 10px 20px;
      cursor: pointer;
      border: none;
      font-weight: 600;
    }

    .btn {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      border-radius: 6px;
      text-decoration: none;
      font-size: 14px;
      transition: opacity 0.2s;
    }

    .btn-ghost {
      background: var(--hover-color);
      color: var(--text-color);
      border: 1px solid var(--border-color);
    }

    .btn-ghost:hover {
      opacity: 0.8;
    }

    .btn-primary {
      background: var(--primary-color);
      color: #fff;
      border: none;
    }

    .btn-primary:hover:not([disabled]) {
      opacity: 0.9;
    }

    .btn[disabled] {
      opacity: 0.6;
      cursor: not-allowed;
    }
  `]
})
export class AdDetailsComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private api = inject(ApiService);

  loading = signal(true);
  error = signal<string | null>(null);
  ad = signal<any>(null);

  // Payment state
  showPaymentModal = signal(false);
  paymentLoading = signal(false);
  paymentError = signal<string | null>(null);
  pixData = signal<{ EncodedImage: string; Payload: string; ExpirationDate: string } | null>(null);
  showCreditCardForm = signal(false);

  // Credit card form state
  creditCardForm = {
    cardNumber: '',
    cvv: '',
    expirationMonth: '',
    expirationYear: '',
    holderName: '',
    cpfCnpj: '',
    email: ''
  };

  ngOnInit() {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.error.set('ID do anúncio não fornecido');
      this.loading.set(false);
      return;
    }

    this.api.getAd(id).pipe(
      finalize(() => this.loading.set(false))
    ).subscribe({
      next: (ad) => {
        this.ad.set(ad);
      },
      error: () => {
        this.error.set('Falha ao carregar o anúncio');
      }
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'Não definido';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  }

  formatMoney(cents: number | null | undefined): string {
    if (cents === null || cents === undefined) return '—';
    const amount = Number(cents) / 100;
    if (Number.isNaN(amount)) return '—';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(amount);
  }

  getStatusLabel(status: string): string {
    const statusMap: { [key: string]: string } = {
      'created': 'Aguardando Pagamento',
      'waiting_payment': 'Aguardando Pagamento',
      'in_analysis': 'Em Análise',
      'approved': 'Aprovado',
      'active': 'Ativo',
      'blocked': 'Bloqueado',
      'finished': 'Finalizado'
    };
    return statusMap[status] || status;
  }

  statusKey(status: string): string {
    if (!status) return 'draft';
    if (['active', 'approved'].includes(status)) return 'active';
    if (['waiting_payment', 'in_analysis', 'created'].includes(status)) return 'pending';
    if (status === 'blocked') return 'blocked';
    if (status === 'finished') return 'expired';
    return 'draft';
  }

  openPaymentModal() {
    const ad = this.ad();
    if (!ad) return;

    // Navigate to checkout page with ad data
    this.router.navigate(['/checkout'], {
      state: {
        adId: ad.id,
        adTitle: ad.title,
        position: ad.position || 'full_screen',
        audience: ad.audience || ad.target || 'user',
        duration: this.calculateDuration(ad.show_from, ad.show_until),
        startDate: ad.show_from,
        endDate: ad.show_until,
        totalAmount: ad.value || ad.total_value || 0,
        imageUrl: ad.image_url,
        purchaseId: ad.purchase_id
      },
      queryParams: { adId: ad.id } // Also pass as query param for page refresh
    });
  }

  private calculateDuration(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  }

  closePaymentModal() {
    this.showPaymentModal.set(false);
    this.paymentError.set(null);
    this.pixData.set(null);
    this.showCreditCardForm.set(false);
  }

  async requestPixQrCode() {
    const purchaseId = this.ad()?.purchase_id;
    if (!purchaseId) {
      this.paymentError.set('ID de compra não encontrado');
      return;
    }

    this.paymentLoading.set(true);
    this.paymentError.set(null);
    try {
      const res = await new Promise<any>((resolve, reject) => {
        this.api.getPixQrCode(purchaseId).subscribe({
          next: resolve,
          error: reject
        });
      });
      this.pixData.set(res.data);
    } catch (err: any) {
      this.paymentError.set(err?.error?.message || 'Falha ao gerar QR Code PIX');
    } finally {
      this.paymentLoading.set(false);
    }
  }

  copyPixCode() {
    const payload = this.pixData()?.Payload;
    if (payload) {
      navigator.clipboard.writeText(payload).then(() => {
        alert('Código PIX copiado para a área de transferência');
      });
    }
  }

  openCreditCardForm() {
    this.showCreditCardForm.set(true);
  }

  closeCreditCardForm() {
    this.showCreditCardForm.set(false);
  }

  async submitCreditCard() {
    const purchaseId = this.ad()?.purchase_id;
    if (!purchaseId) {
      this.paymentError.set('ID de compra não encontrado');
      return;
    }

    // Basic validation
    if (!this.creditCardForm.cardNumber || !this.creditCardForm.cvv || 
        !this.creditCardForm.expirationMonth || !this.creditCardForm.expirationYear ||
        !this.creditCardForm.holderName || !this.creditCardForm.cpfCnpj || !this.creditCardForm.email) {
      this.paymentError.set('Por favor, preencha todos os campos');
      return;
    }

    this.paymentLoading.set(true);
    this.paymentError.set(null);

    try {
      const payload = {
        payment_id: purchaseId,
        card_data: {
          number: this.creditCardForm.cardNumber.replace(/\s/g, ''),
          cvv: this.creditCardForm.cvv,
          expiration_month: this.creditCardForm.expirationMonth,
          expiration_year: this.creditCardForm.expirationYear
        },
        holder_data: {
          name: this.creditCardForm.holderName,
          cpf_cnpj: this.creditCardForm.cpfCnpj.replace(/\D/g, '')
        },
        email: this.creditCardForm.email
      };

      await new Promise<any>((resolve, reject) => {
        this.api.payWithCreditCard(payload).subscribe({
          next: resolve,
          error: reject
        });
      });

      // Success - reload ad to get updated status
      this.closePaymentModal();
      this.reloadAd();
      alert('Pagamento realizado com sucesso!');
    } catch (err: any) {
      this.paymentError.set(err?.error?.message || 'Falha ao processar pagamento com cartão');
    } finally {
      this.paymentLoading.set(false);
    }
  }

  private reloadAd() {
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.api.getAd(id).subscribe({
        next: (ad) => this.ad.set(ad),
        error: () => this.error.set('Falha ao recarregar anúncio')
      });
    }
  }
}
